// Primary navigation sidebar (wide layout). Narrow layout uses the header <select>.

import { C, MONO } from '../lib/tokens';
import { useData, useTradingVpsDataAgeS, useUi } from '../store/hooks';
import { navGo, type NavKey } from '../store/uiStore';
import { activeCriticalCount } from '../lib/selectors';
import { summarizeTradingVps, tradingVpsStaleAgeS } from '../lib/tradingVps';

interface NavDef {
  key: NavKey;
  glyph: string;
  label: string;
  badge: string;
  badgeColor?: string;
}

export function useNavDefs(): NavDef[] {
  const accountsCount = useData((s) => s.accounts.length);
  const positions = useData((s) => s.accounts.reduce((n, a) => n + a.positions_count, 0));
  const crit = useData((s) => activeCriticalCount(s.alerts));
  const tradingVps = useData((s) => s.tradingVps);
  // A stale poll feed degrades the badge too — never a frozen green "N/N" while unreachable.
  const tradingStaleAgeS = tradingVpsStaleAgeS(useTradingVpsDataAgeS());
  const tradingSummary = summarizeTradingVps(tradingVps, tradingStaleAgeS);
  return [
    { key: 'overview', glyph: '◫', label: 'Overview', badge: '' },
    {
      key: 'tradingVps', glyph: '▦', label: 'Trading VPS',
      badge: tradingSummary.issues ? `!${tradingSummary.issues}` : `${tradingSummary.live}/${tradingSummary.total}`,
      badgeColor: tradingSummary.issues ? C.rd : tradingSummary.total ? C.gn : C.dim2,
    },
    { key: 'accounts', glyph: '≡', label: 'Accounts', badge: String(accountsCount) },
    { key: 'positions', glyph: '⇅', label: 'Open Positions', badge: String(positions) },
    { key: 'alerts', glyph: '⚑', label: 'Alerts', badge: crit ? String(crit) : '' },
    { key: 'vps', glyph: '⌗', label: 'Collectors & Terminals', badge: '' },
    { key: 'detail', glyph: '▣', label: 'Account Detail', badge: '' },
    { key: 'settings', glyph: '⚙', label: 'Settings', badge: '' },
    { key: 'docs', glyph: '¶', label: 'Docs', badge: '' },
  ];
}

export function Sidebar() {
  const nav = useUi((s) => s.nav);
  const items = useNavDefs();
  const crit = useData((s) => activeCriticalCount(s.alerts));
  const collectorVersion = useData((s) => s.vps[0]?.collector_version ?? '—');
  const build = useData((s) => s.accounts[0]?.terminal_build ?? null);

  return (
    <nav
      aria-label="Primary"
      style={{
        width: 198,
        minWidth: 198,
        background: '#0A0F19',
        borderRight: '1px solid ' + C.ln2,
        display: 'flex',
        flexDirection: 'column',
        padding: '12px 9px',
        gap: 1,
      }}
    >
      <div style={{ padding: '4px 9px 14px' }}>
        <div style={{ fontFamily: MONO, fontSize: 12.5, letterSpacing: '.24em', fontWeight: 600 }}>WATCHTOWER</div>
        <div style={{ fontSize: 10, color: C.dm, letterSpacing: '.08em', marginTop: 3 }}>MT5 FLEET MONITOR</div>
      </div>
      {items.map((n) => {
        const current = nav === n.key;
        return (
          <button
            key={n.key}
            onClick={() => navGo(n.key)}
            aria-current={current ? 'page' : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              padding: '7px 9px',
              borderRadius: 6,
              border: 'none',
              background: current ? C.s2 : 'none',
              color: current ? C.tx : C.dm,
              fontSize: 12.5,
              fontWeight: current ? 600 : 500,
              fontFamily: 'inherit',
            }}
          >
            <span style={{ fontFamily: MONO, fontSize: 12, width: 16, display: 'inline-block', textAlign: 'center' }} aria-hidden="true">
              {n.glyph}
            </span>
            <span style={{ flex: 1, textAlign: 'left' }}>{n.label}</span>
            <span style={{ fontFamily: MONO, fontSize: 10, color: n.badgeColor ?? (n.key === 'alerts' && crit ? C.rd : C.dim2), fontWeight: 600 }}>
              {n.badge}
            </span>
          </button>
        );
      })}
      <div style={{ flex: 1 }} />
      <div style={{ padding: 9, borderTop: '1px solid ' + C.ln2, fontSize: 10, color: C.dm, fontFamily: MONO, lineHeight: 1.7 }}>
        collector {collectorVersion}
        {build != null && (
          <>
            <br />
            MT5 build {build}
          </>
        )}
      </div>
    </nav>
  );
}
